import { AiOutlinePlus } from "react-icons/ai";
import EditPayPeriod from "./EditPayPeriod";
import NewPayPeriod from "./NewPayPeriod";
import SkewLoader from "react-spinners/SkewLoader";
import { useState } from "react";
import { PayPeriodDto } from "../../../../../models/pay-period.dto";
import { useGetPayPeriod } from "../../../../../services/pay-period.service";
import { APP_CONFIG } from "../../../../../helpers/appHelpers";
import { useGetTaxCode } from "../../../../../services/tax-code.service";
import { TaxCodeOrderBy } from "../../../../../models/tax-code.dto";
import useCompanyStore from "../../../../../state-management/useCompanyStore";
import CustomPagination from "../../../../../components/table/CustomPagination";
import { useManageAccordions } from "../../../../../helpers/useManageAccordions";
import {
	useCheckUserPermissions,
	PermissionChecker,
	PERMISSIONS,
} from "../../../../../helpers/usePermissionManager";
import AutoGeneratePayPeriodsModal from "./modals/AutoGeneratePayPeriodsModal";
import { CalendarMonth } from "@mui/icons-material";

// Status is driven by the backend field payPeriod.status — never derived from
// dates client-side. A pay period must be explicitly opened/closed via an API
// action; it does NOT open or close automatically based on its date range.

const PayPeriodStatusBadge = ({ payPeriod }: { payPeriod: PayPeriodDto }) => {
	const status = payPeriod.status;

	const isOpen = status === "OPEN";
	const isUpcoming = status === "UPCOMING";

	const label = isOpen ? "Open" : isUpcoming ? "Upcoming" : "Closed";
	const colors = isOpen
		? "bg-green-100 text-green-700"
		: isUpcoming
			? "bg-blue-100 text-blue-600"
			: "bg-gray-100 text-gray-500";
	const dotColor = isOpen
		? "bg-green-500"
		: isUpcoming
			? "bg-blue-500"
			: "bg-gray-400";

	return (
		<span
			className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${colors}`}
		>
			<span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />
			{label}
		</span>
	);
};

const PayPeriods = () => {
	const { currentCompany } = useCompanyStore();
	const { checkUserPermissionsAnd } = useCheckUserPermissions();
	const [showNewPayPeriodForm, setShowNewPayPeriodForm] = useState(false);
	const [openAutoGenerate, setOpenAutoGenerate] = useState(false);

	const {
		accordionsData: payPeriods,
		isLoading,
		pageCount,
		setAccordionsData,
	} = useManageAccordions<PayPeriodDto>({
		fetchHook: useGetPayPeriod,
		options: { companyId: currentCompany?.id },
	});

	const { data: taxCodes, isLoading: loadingTaxCodes } = useGetTaxCode({
		countryId: currentCompany?.countryId as number,
		limit: APP_CONFIG.PAGE_LIMIT,
		orderBy: TaxCodeOrderBy.CREATED_AT_DESC,
	});

	return (
		<div>
			<div className="border-b-[1.5px] pb-3 mb-4 flex w-full justify-between text-sm">
				<h2 className="font-semibold">Available Pay Periods</h2>

				<div className="flex items-center gap-4">
					<PermissionChecker permissions={[PERMISSIONS.COMPANY_CONFIGS_WRITE]}>
						<button
							onClick={() => setOpenAutoGenerate(true)}
							className="border border-gray-300 rounded-lg text-[12px] text-gray-600 py-2 px-3 flex items-center hover:bg-gray-50"
						>
							<CalendarMonth
								sx={{ fontSize: 15, mr: 0.8, color: "text.secondary" }}
							/>
							Auto-Generate Periods
						</button>
					</PermissionChecker>

					{!showNewPayPeriodForm && (
						<PermissionChecker
							permissions={[PERMISSIONS.COMPANY_CONFIGS_WRITE]}
						>
							<button
								onClick={() => setShowNewPayPeriodForm(true)}
								className="border-none flex items-center text-primary-blue"
							>
								<AiOutlinePlus className="mr-2 text-lg" />
								Add New Pay Period
							</button>
						</PermissionChecker>
					)}
				</div>
			</div>

			{showNewPayPeriodForm && (
				<NewPayPeriod
					taxCodes={taxCodes?.data || []}
					setShowNewPayPeriodForm={setShowNewPayPeriodForm}
				/>
			)}

			{isLoading &&
				!payPeriods.length &&
				checkUserPermissionsAnd([PERMISSIONS.COMPANY_CONFIGS_READ]) && (
					<div className="text-center mt-10 py-10">
						<SkewLoader color="#F58B00" />
					</div>
				)}

			{!loadingTaxCodes && (
				<>
					{payPeriods.map((payPeriod) => (
						<div key={payPeriod.id} className="relative mb-2">
							{/* Status badge positioned to the left of the expand chevron */}
							<div className="absolute right-12 top-[10px] z-10 pointer-events-none">
								<PayPeriodStatusBadge payPeriod={payPeriod} />
							</div>
							<EditPayPeriod
								payPeriod={payPeriod}
								taxCodes={taxCodes?.data || []}
							/>
						</div>
					))}
				</>
			)}

			{!isLoading &&
				!payPeriods.length &&
				checkUserPermissionsAnd([PERMISSIONS.COMPANY_CONFIGS_READ]) && (
					<div>
						<h3 className="text-center text-sm font-semibold mt-10">
							Sorry no pay periods added yet. Click 'Add New Pay Period' to
							begin.
						</h3>
					</div>
				)}

			{checkUserPermissionsAnd([PERMISSIONS.COMPANY_CONFIGS_READ]) ? (
				<div className="w-full flex justify-end mt-8">
					<CustomPagination pageCount={pageCount} />
				</div>
			) : (
				<div>
					<h3 className="text-center text-sm font-semibold mt-10">
						You are not authorized to access this data.
					</h3>
				</div>
			)}

			{openAutoGenerate && (
				<AutoGeneratePayPeriodsModal
					open={openAutoGenerate}
					setOpen={setOpenAutoGenerate}
					onSuccess={() => setAccordionsData([])}
				/>
			)}
		</div>
	);
};

export default PayPeriods;
